import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getFulfillmentLabel, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Info from "@insite/mobius/Icons/Info";
import MapPin from "@insite/mobius/Icons/MapPin";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    fulfillmentLabel: getFulfillmentLabel(state),
    isDrawerOpen: state.components.addressDrawer.isOpen,
    isPunchOutSession: getIsPunchOutSession(state),
    enableHubWarehouse: state.context.settings.properties.enableHubWarehouse.toLowerCase() === "false" ? false : true,
    redirectToChangeCustomerPageOnSignIn: state.context.session.redirectToChangeCustomerPageOnSignIn,
});

const mapDispatchToProps = {
    setDrawerIsOpen,
};

const enum fields {
    closeIconCss = "closeIconCss",
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleNonClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    modalSignIn?: InjectableCss;
    modalIcon?: IconPresentationProps;
    infoMessage?: InjectableCss;
    closebutton?: InjectableCss;
    info?: InjectableCss;
    message?: InjectableCss;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "rounded",
        buttonType: "solid",
        color: "common.background",
        size: 36,
        css: css`
            border-radius: 50%;
            padding: 0 10px !important;
            margin-right: 5px;
        `,
    },
    closeButtonIconProps: {
        src: "X",
        size: 16,
        css: css`
            &:active {
                color: #fcfcfc !important;
            }
        `,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            position: relative;
            display: inline-flex;
            width: 100% !important;
        `,
    },
    titleClickable: {
        css: css`
            width: 100%;
            padding: 6px 0;
            color: white;
            position: relative;
        `,
    },
    titleNonClickable: {
        css: css`
            width: 100%;
            padding: 6px 0;
            color: white;
            position: relative;
            cursor: default;
            &:focus{
                outline: none !important;
            }
        `,
    },
    titleIcon: {
        size: 22,
        css: css`
            color: white;
            margin-right: 10px;
        `,
    },
    titleTypography: {
        css: css`
            color: #fc3;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    drawer: baseDrawerStyles,
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
    modalSignIn: {
        css: css`
            position: absolute;
            top: 150%;
            left: -120px;
            width: 375px !important;
            display: inline-block;
            line-height: 1.5;
            right: 50px;
            background-color: white;
            box-shadow: 0px 8px 8px rgba(0, 0, 0, 0.15);
            z-index: 101;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 15px;
            align-items: center;
            border-style: solid;
            border-width: 1px;
            border-color: #64b5f6;
            border-width: 1px;
            border-radius: 8px;
            ::before {
                border-color: transparent transparent white;
                border-style: solid;
                border-width: 0 1rem 1.2rem;
                content: "";
                height: 0;
                left: 0;
                margin: -1rem auto 0;
                position: absolute;
                right: 0;
                top: 0;
                width: 0;
            }
        `,
    },
    modalIcon: {
        size: 22,
        css: css`
            color: black;
            margin-left: 16px;
            margin-right: 10px;
        `,
    },
    closebutton: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    infoMessage: {
        css: css`
            display: flex;
            justify-content: flex-start;
            padding-bottom: 15px !important;
            font-size: 15px !important;
        `,
    },
    info: {
        css: css`
            color: rgb(59 130 246);
            margin-right: 15px;
            margin-top: 3px;
            size: 24px;
        `,
    },
    message: {
        css: css`
            width: 80%;
        `,
    },
};

const defaultCustomCss = `color: #474747 !important;`;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.closeIconCss]: string;
    };
}

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const HeaderShipToAddress: FC<Props> = ({
    id,
    fields,
    fulfillmentLabel,
    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    isPunchOutSession,
    enableHubWarehouse,
    redirectToChangeCustomerPageOnSignIn,
}) => {
    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (redirectToChangeCustomerPageOnSignIn) {
            setDrawerIsOpen({ isOpen: !isDrawerOpen });
        }
    };

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
    };

    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);

    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    const [isVisible, setIsVisible] = React.useState(false);

    const shipFromMessageCloseHandler = () => {
        setIsVisible(false);
        sessionStorage.setItem("IsShipFromMessageVisible", "false");
    };

    useEffect(() => {
        const val = sessionStorage.getItem("IsShipFromMessageVisible");
        if (val === "" || val === "false") {
            setIsVisible(false);
        } else {
            setIsVisible(true);
        }
    }, [isVisible]);

    const tittleStyle = redirectToChangeCustomerPageOnSignIn ? styles.titleClickable : styles.titleNonClickable;

    return (
        <>
            <StyledWrapper {...styles.shipToAddressWrapper}>
                <Clickable {...tittleStyle} onClick={handleClickAddress} data-test-selector="header_shipToButton">
                    {/* As this doesn't look final, but is the pattern on which the mobile nav drawer was based,
                    please also make changes to `NavigationDrawer.tsx` when you change this. */}
                    <Icon {...styles.titleIcon} src={MapPin} />
                    <Typography {...styles.titleTypography} data-test-selector="header_shipToLabel">
                        {fulfillmentLabel}
                    </Typography>
                </Clickable>

                {/* EC-1061 - Start - Hide pricing message for non-logged in user - Hub Warehouse Changes*/}
                {isVisible && !enableHubWarehouse && (
                    <StyledWrapper {...styles.modalSignIn}>
                        <StyledWrapper {...styles.closebutton}>
                            <Clickable onClick={() => shipFromMessageCloseHandler()}>
                                <Icon {...styles.modalIcon} src={X} />
                            </Clickable>
                        </StyledWrapper>
                        <StyledWrapper {...styles.infoMessage}>
                            <Icon {...styles.modalIcon} src={Info} {...styles.info} />
                            <Typography {...styles.message}>{siteMessage("ShipFromBranch_Message")}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {/* EC-1061 - End - Hide pricing message for non-logged in user - Hub Warehouse Changes*/}
            </StyledWrapper>
            <Drawer
                {...drawerStyles}
                closeButtonIconProps={{
                    ...drawerStyles?.closeButtonIconProps,
                    css: css`
                        ${fields.closeIconCss}
                    `,
                }}
                position="top"
                isOpen={isDrawerOpen}
                headline={isPunchOutSession ? "" : translate("Change Customer")}
                handleClose={handleCloseDrawer}
                data-test-selector="addressDrawer"
            >
                <Zone zoneName="Container" contentId={id} />
            </Drawer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.closeIconCss,
                displayName: "Close Icon Css",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            },
        ],
    },
};

export default widgetModule;
